import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useTheme } from '@mui/material/styles';
import { sendClientInvite } from '../../../company-management/invitations/services/invitationsApi';
import '../../../company-management/invitations/styles/dialog.css';

export default function InviteClientDialog({ open, onClose, companyData, onInvited, description }) {
  const { user } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Ensure data-theme attribute is set for CSS variables (must be before early return)
  useEffect(() => {
    if (isDark) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, [isDark]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleInvite = async () => {
    // Validate email format
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!isValidEmail(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await sendClientInvite({
        email: email.trim(),
        companyId: companyData?.id,
        ownerId: companyData?.ownerId,
        inviterName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
        currentUser: user,
      });
      onInvited?.();
      onClose?.();
      setEmail('');
    } catch (err) {
      setError(err?.message || 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop">
      <div 
        className="modal-card" 
        role="dialog" 
        aria-modal="true" 
        aria-labelledby="invite-client-title"
        data-theme={isDark ? 'dark' : 'light'}
      >
        <div className="modal-header">
          <div className="modal-title" id="invite-client-title">Invite Client</div>
        </div>
        <div className="modal-body">
          {description && (
            <div className="dialog-description" style={{ marginBottom: 12, fontSize: 14 }}>
              {description}
            </div>
          )}
          <div className="field">
            <label className="label">Client Email</label>
            <input className="input" type="email" placeholder="name@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          {error && <div className="error-text">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button 
            className="btn btn-primary" 
            onClick={handleInvite} 
            disabled={loading || !email.trim() || !isValidEmail(email.trim())}
          >
            {loading ? <span className="spinner" /> : null}
            {loading ? 'Sending...' : 'Send Invite'}
          </button>
        </div>
      </div>
    </div>
  );
}


